// src/components/form/QuestionStep.js
import React, { useRef, useEffect, memo } from "react";
import renderInputField from "../../utils/form/formFields";
import { useTranslation } from "react-i18next";

/**
 * QuestionStep
 * ------------
 * Renders a single question (text, number, date, select, or range) and its
 * corresponding input. The exact field rendered is determined by
 * `question.fieldType`.
 *
 * Props
 * -----
 * • `question`   (object) – Question definition with at least:
 *       { id, title, fieldType, required, ... }
 * • `value`      (any)    – Current answer (will be stringified in <input>).
 * • `onChange`   (fn)     – Pass-through change handler for all inputs.
 * • `onEnter`    (fn)     – Fired when user presses Enter inside any input.
 * • `autoFocus`  (bool)   – Auto-focus the field on mount.
 * • `className`  (string) – Extra CSS classes for the wrapper.
 *
 * Notes
 * -----
 * • Re-renders are memoised via React.memo; component updates only when
 *   `question` or `value` (prop identity) changes.
 * • The actual **input factory** lives in the `renderInputField()` helper
 *   so you can move / unit-test it separately later.
 */

/* ------------------------------------------------------------------ */
/* Main component                                                     */
/* ------------------------------------------------------------------ */
function QuestionStep({
  question,
  value,
  onChange,
  autoFocus = false,
  onEnter,
  translationForm = "form",
}) {
  const { t } = useTranslation(translationForm); // <<< point to the “form” namespace
  const { t: tCommon } = useTranslation();

  const inputRef = useRef(null);

  /* Auto-focus ------------------------------------------------------ */
  useEffect(() => {
    if (autoFocus && inputRef.current) inputRef.current.focus();
  }, [autoFocus]);

  return (
    <div className={`question-step space-y-2`}>
      <h2 className="text-xl font-bold">
        {t(question.titleKey)}
        {question.required && (
          <span className="muted-text"> {tCommon("form.required")}</span>
        )}
      </h2>

      {question.descriptionKey && (
        <p className="text-sm text-greyscale-3 whitespace-pre-line">
          {t(question.descriptionKey)}
        </p>
      )}

      {question.resourceUrl && (
        <img
          src={question.resourceUrl}
          alt=""
          className="w-full h-full rounded-lg"
        />
      )}

      {renderInputField({
        question,
        value,
        onChange,
        onEnter,
        inputRef,
        t,
      })}
    </div>
  );
}

export default memo(QuestionStep);
